import { checkNodeValid } from "./general";
import { MinQueue } from "heapify";

export function uniformCost(grid, getNeighboursFn, startNode) {
  const visitedNodes = []; // store the visited nodes in order (to be animated)
  const frontierNodes = []; // store the current nodes that are on the front lines
  frontierNodes.push(startNode);
  startNode.isVisited = true;
  startNode.distance = 0;

  while (frontierNodes.length) {
    const currentNode = frontierNodes.shift();
    const neighbours = getNeighboursFn(grid, currentNode);
    for (let neighbour of neighbours) {
      if (!checkNodeValid(neighbour.node)) continue;
      neighbour.node.isVisited = true;
      neighbour.node.parentNode = currentNode;
      neighbour.node.distance = Math.min(
        currentNode.distance + neighbour.extraDist,
        neighbour.node.distance
      );

      frontierNodes.push(neighbour.node);
      if (neighbour.node.type === "finish") return visitedNodes;
      visitedNodes.push(neighbour.node);
    }
  }
  return visitedNodes;
}

export function aStar(grid, getNeighboursFn, startNode, finishNode, heuristic) {
  // define helper function to generate an unsigned int ID for each node
  function generateNodeId(node) {
    return node.row * grid[0].length + (node.col + 1);
  }
  const gridSize = grid.length * grid[0].length; // used to set the size of Heapify min heap
  const visitedNodes = []; // store the visited nodes in order (to be animated)
  const frontierNodes = new MinQueue(gridSize); // store frontier nodes in a min heap priority queue
  // our float heuristic gets transformed into an integer for heapify, want to keep precision
  const heuristicMultiplier = 1000; // 1000 = 3dp precision, scale is irrelevant
  const nodeIdMap = {}; // link nodes to their generated int IDs so that we can use Heapify
  const startNodeId = generateNodeId(startNode);
  nodeIdMap[startNodeId] = startNode;
  frontierNodes.push(
    startNodeId,
    heuristic(startNode, finishNode) * heuristicMultiplier
  );
  startNode.isVisited = true;
  startNode.distance = 0;

  while (frontierNodes.length) {
    const currentNodeId = frontierNodes.pop();
    const currentNode = nodeIdMap[currentNodeId];
    const neighbours = getNeighboursFn(grid, currentNode);
    for (let neighbour of neighbours) {
      if (!checkNodeValid(neighbour.node)) continue;
      neighbour.node.isVisited = true;
      neighbour.node.parentNode = currentNode;
      neighbour.node.distance = Math.min(
        currentNode.distance + neighbour.extraDist,
        neighbour.node.distance
      );
      const neighbourId = generateNodeId(neighbour.node);
      nodeIdMap[neighbourId] = neighbour.node;
      const tieBreak = 1.01; // make the algorithm prefer heuristic slightly
      frontierNodes.push(
        neighbourId,
        (heuristic(neighbour.node, finishNode) * tieBreak +
          neighbour.node.distance) *
          heuristicMultiplier
      );
      // document.getElementById(
      //   `node-${neighbour.node.row}-${neighbour.node.col}`
      // ).innerText =
      //   String(
      //     Math.round(
      //       heuristic(neighbour.node, finishNode) *
      //         tieBreak *
      //         heuristicMultiplier
      //     )
      //   ) +
      //   ", " +
      //   String(Math.round(neighbour.node.distance * heuristicMultiplier)) +
      //   ", " +
      //   String(
      //     Math.round(
      //       (neighbour.node.distance +
      //         heuristic(neighbour.node, finishNode) * tieBreak) *
      //         heuristicMultiplier
      //     )
      //   );

      if (neighbour.node.type === "finish") return visitedNodes;
      visitedNodes.push(neighbour.node);
    }
  }
  return visitedNodes;
}

export function aStar2(grid, getNeighboursFn, startNode, finishNode, heuristic) {
  const rows     = grid.length;
  const cols     = grid[0].length;
  const nodeId   = n => n.row * cols + n.col;
  const startId  = nodeId(startNode);
  const goalId   = nodeId(finishNode);
  const maxPush  = rows * cols * 4;

  const openHeap     = new MinQueue(maxPush);
  const gScore       = new Map();
  const closedSet    = new Set();
  const idToNode     = {};
  const visitedOrder = [];

  idToNode[startId] = startNode;
  idToNode[goalId]  = finishNode;

  // init
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      gScore.set(r * cols + c, Infinity);
    }
  }
  gScore.set(startId, 0);
  openHeap.push(startId, heuristic(startNode, finishNode));

  while (openHeap.size) {
    const currentId = openHeap.pop();
    // ◀️ skip any old/worse entry
    if (closedSet.has(currentId)) continue;

    const currentNode = idToNode[currentId];
    const currentG    = gScore.get(currentId);

    // ◀️ record expansion (or move this into the neighbor loop for discovery order)
    visitedOrder.push(currentNode);

    // goal test
    if (currentId === goalId) return visitedOrder;

    closedSet.add(currentId);

    for (let { node: nb, extraDist } of getNeighboursFn(grid, currentNode)) {
      if (!checkNodeValid(nb)) continue;

      const nbId = nodeId(nb);
      if (closedSet.has(nbId)) continue;

      const tentativeG = currentG + extraDist;
      if (tentativeG < gScore.get(nbId)) {
        // ◀️ if you want DISCOVERY order, do:
        // visitedOrder.push(nb);

        idToNode[nbId] = nb;
        gScore.set(nbId, tentativeG);
        const fScore = tentativeG + heuristic(nb, finishNode);
        openHeap.push(nbId, fScore);
      }
    }
  }

  return visitedOrder;
}

